import { purryFromLazy } from "./internal/purryFromLazy";
import type { LazyEvaluator } from "./internal/types/LazyEvaluator";
import { SKIP_ITEM } from "./internal/utilityEvaluators";

type Comparator<TFirst, TSecond> = (a: TFirst, b: TSecond) => boolean;

/**
 * Returns a list of intersecting values based on a custom
 * comparator function that compares elements of both arrays.
 *
 * @param array - The source array.
 * @param other - The second array.
 * @param comparator - The custom comparator.
 * @signature
 *    intersectionWith(array, other, comparator)
 * @example
 *    intersectionWith(
 *      [
 *        { id: 1, name: 'Ryan' },
 *        { id: 3, name: 'Emma' },
 *      ],
 *      [3, 5],
 *      (a, b) => a.id === b,
 *    ) // => [{ id: 3, name: 'Emma' }]
 * @dataFirst
 * @lazy
 * @category Array
 */
export function intersectionWith<TFirst, TSecond>(
  array: readonly TFirst[],
  other: readonly TSecond[],
  comparator: Comparator<TFirst, TSecond>,
): TFirst[];

/**
 * Returns a list of intersecting values based on a custom
 * comparator function that compares elements of both arrays.
 *
 * @param other - The second array.
 * @param comparator - The custom comparator.
 * @signature
 *    intersectionWith(other, comparator)(array)
 * @example
 *    intersectionWith(
 *      [3, 5],
 *      (a, b) => a.id === b
 *      )([
 *        { id: 1, name: 'Ryan' },
 *        { id: 3, name: 'Emma' },
 *      ]); // => [{ id: 3, name: 'Emma' }]
 * @dataLast
 * @lazy
 * @category Array
 */
export function intersectionWith<TFirst, TSecond>(
  other: readonly TSecond[],
  /**
   * Type inference doesn't work properly for the comparator's first parameter
   * in data last variant.
   */
  comparator: Comparator<TFirst, TSecond>,
): (array: readonly TFirst[]) => TFirst[];

export function intersectionWith(...args: readonly unknown[]): unknown {
  return purryFromLazy(lazyImplementation, args);
}

const lazyImplementation =
  <TFirst, TSecond>(
    other: readonly TSecond[],
    comparator: Comparator<TFirst, TSecond>,
  ): LazyEvaluator<TFirst> =>
  (value) =>
    other.some((otherValue) => comparator(value, otherValue))
      ? { done: false, hasNext: true, next: value }
      : SKIP_ITEM;
